<script setup lang="ts">
  import { MpLoadingContainer } from '@borg/ui';

  const articleId = useRoute().params.adviceId as string;

  const {
    data: article,
    pending,
    error,
    refresh,
    status,
  } = await articleDetailsRepository.get(articleId);
  await relatedArticlesRepository.getMany(articleId);

  const { t } = useI18n();
  const domain = useDomain();
  const route = useRoute();

  const pageTitle = computed(() => article.value?.title ?? t('ADVICE_PAGE.TITLE'));
  const pageDescription = computed(() => article.value?.intro ?? t('ADVICE_PAGE.DESCRIPTION'));
  const articleImageUrl = computed(() =>
    article.value?.imageUrl
      ? {
          url: article.value.imageUrl,
          width: 820,
          height: 430,
        }
      : undefined,
  );

  watchEffect(() => {
    usePageHead({
      pageName: pageTitle.value,
      description: pageDescription.value,
    });
  });

  useHead({
    link: [{ rel: 'canonical', href: `${domain.value}${route.path}`.replace(/\/$/, '') }],
  });

  useSeoMeta({
    ogTitle: pageTitle,
    ogDescription: pageDescription,
    ogType: 'article',
    ogImage: articleImageUrl,
  });

  async function fixSlug() {
    const route = useRoute();
    const routeSlug = (route.params.slug?.toString() ?? '').toLowerCase();
    const articleSlug = (article.value?.slug ?? '').toLowerCase();

    if (article.value && routeSlug !== articleSlug) {
      const getRouteBaseName = useRouteBaseName();
      const routeBaseName = getRouteBaseName(route);
      logger.info(
        `Fix article slug on '${routeBaseName}' :: ${articleId} :: ${route.params.slug} ==> ${article.value.slug}`,
      );
      await navigate(
        {
          name: 'advices-adviceId-slug',
          params: { slug: article.value.slug },
          query: route.query,
        },
        { replace: true },
      );
    }
  }

  watch(
    status,
    async (value) => {
      if (value === 'success') {
        await fixSlug();
      } else if (value === 'error') {
        showError({
          statusCode: 404,
          message: 'Job not found',
        });
      }
    },
    { immediate: true },
  );
</script>

<template>
  <MpLoadingContainer
    :is-loading="pending"
    :has-error="!!error"
    @reload="refresh"
  >
    <NuxtPage />
  </MpLoadingContainer>
</template>
